import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { ref, onValue, off } from "firebase/database";
import { database } from "../constants/firebase";
import { useUser } from "../contexts/UserContext";
import Sidebar from "../components/Sidebar";
import MobileHeader from "../components/MobileHeader";
import useMoveTo from "../constants/navigation";
import { ICONS } from "../constants/icons";
import { LuUsers, LuExternalLink, LuTrophy } from "react-icons/lu";

const ChallengeApplicants = () => {
    const { monthKey } = useParams();
    const { currentUser } = useUser();
    const navigate = useMoveTo();

    const [challenge, setChallenge] = useState(null);
    const [applicants, setApplicants] = useState([]);
    const [loading, setLoading] = useState(true);

    // Load challenge details
    useEffect(() => {
        if (!monthKey) return;
        const challengeRef = ref(database, `Challenges/${monthKey}`);
        onValue(challengeRef, (snap) => {
            if (snap.exists()) {
                const data = snap.val();
                setChallenge({ id: monthKey, ...data });
                
                // Parse applicants
                if (data.applications) {
                    const list = Object.entries(data.applications)
                        .map(([uid, app]) => ({ uid, ...app }))
                        .sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));
                    setApplicants(list);
                } else {
                    setApplicants([]);
                }
            }
            setLoading(false);
        });
        return () => off(challengeRef);
    }, [monthKey]);

    if (!currentUser?.isAdmin) {
        return <div className="h-screen flex items-center justify-center dark:bg-[#1A1A1A] dark:text-white">Access Denied</div>;
    }

    return (
        <div className="flex flex-col h-screen bg-white text-black dark:bg-[#1A1A1A] dark:text-white overflow-hidden">
            <MobileHeader
                title={challenge ? `${challenge.title} — Applicants` : "Applicants"}
                isBackBtn
            />
            <div className="flex flex-1 overflow-hidden">
                <Sidebar />
                <main className="flex-1 overflow-y-auto pb-24 md:pb-5 px-4 pt-5 bg-white dark:bg-[#1A1A1A]">
                    {/* Desktop header */}
                    <div className="flex items-center justify-between mb-8">
                        <button onClick={() => navigate(-1)} className="flex-shrink-0 md:block hidden">
                            <img
                                src={ICONS.arrowBack}
                                className="w-4 h-4 md:w-5 md:h-5 grayscale brightness-0 dark:invert"
                                alt="Back"
                            />
                        </button>
                        <div className="hidden md:block text-center flex-1">
                            <h2 className="text-2xl md:text-3xl font-bold flex items-center justify-center gap-3">
                                <LuTrophy className="text-[#327691]" />
                                {challenge ? challenge.title : "Applicants"}
                            </h2>
                            {challenge && (
                                <p className="text-sm text-gray-400 mt-1 uppercase tracking-widest font-bold">
                                    Month: {challenge.id} · {applicants.length} Submissions
                                </p>
                            )}
                        </div>
                        <div className="w-5" />
                    </div>

                    {loading ? (
                        <div className="flex justify-center pt-20">
                            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#327691]" />
                        </div>
                    ) : applicants.length === 0 ? (
                        <div className="flex flex-col items-center pt-24 text-center">
                            <LuUsers className="w-16 h-16 text-gray-200 dark:text-gray-700 mb-4" />
                            <p className="text-gray-500 dark:text-gray-400 text-lg font-bold">
                                No applications found
                            </p>
                            <p className="text-gray-400 dark:text-gray-500 text-sm mt-2">
                                When creative professionals apply, they will appear here.
                            </p>
                        </div>
                    ) : (
                        <div className="max-w-4xl mx-auto space-y-4">
                            {applicants.map((app, idx) => (
                                <div
                                    key={idx}
                                    className="p-5 rounded-2xl border border-gray-100 dark:border-gray-800 bg-white dark:bg-[#1f1f1f] hover:shadow-md transition-all group"
                                >
                                    <div className="flex items-center justify-between gap-4">
                                        <div className="flex items-center gap-4">
                                            <div className="w-12 h-12 rounded-full bg-gray-100 dark:bg-white/5 flex items-center justify-center font-bold text-[#327691] text-lg">
                                                {app.name?.[0]?.toUpperCase() || "?"}
                                            </div>
                                            <div>
                                                <h4 className="font-bold text-base">{app.name}</h4>
                                                <p className="text-sm text-gray-500">{app.email}</p>
                                            </div>
                                        </div>
                                        
                                        <div className="flex items-center gap-4">
                                            <div className="hidden md:block text-right">
                                                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Submitted</p>
                                                <p className="text-sm font-medium">{app.timestamp ? new Date(app.timestamp).toLocaleDateString() : 'N/A'}</p>
                                            </div>
                                            <a
                                                href={app.link}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="flex items-center gap-2 bg-[#327691] text-white px-5 py-2.5 rounded-xl font-bold shadow-lg shadow-[#327691]/10 hover:bg-[#2a637a] transition-all"
                                            >
                                                <span>View Work</span>
                                                <LuExternalLink className="w-4 h-4" />
                                            </a>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </main>
            </div>
        </div>
    );
};

export default ChallengeApplicants;
